import { isPlainObject } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { createSearchParams } from 'react-router-dom';
import { COMPANY_FILTER_GROUPS } from '../../components/Filters/Filters.constants';
import { splitFiltersIntoTypeGroups } from '../../components/Filters/Filters.logic';
import {
  CoverageOption,
  CoverageOperationOption,
  CSViewOption,
  FilterRange,
  Filters,
  IssuersFilterOption,
  SearchTextOption,
  WatchListOption,
} from '../../models';
import { isOptionOfAnyFilterType, validateRangeValues } from '../../utils/filterUtils';

export const addSearchParams = (searchParams: URLSearchParams, option: string, ids: string[]): URLSearchParams => {
  const params = createSearchParams(searchParams);
  params.delete(option);
  ids.forEach((id) => params.append(option, id));
  return params;
};

export const addTagSearchParams = (
  searchParams: URLSearchParams,
  tags: Filters['tags'],
  filterTypes: IssuersFilterOption[]
): URLSearchParams => {
  const tagFilterTypes = filterTypes.filter(isOptionOfAnyFilterType);
  const tagFilters = tagFilterTypes.reduce<Record<string, string[]>>((acc, filterType) => {
    acc[filterType] = tags?.[filterType] ?? [];
    return acc;
  }, {});
  let params = searchParams;
  Object.entries(tagFilters).forEach(([option, ids]) => {
    params = addSearchParams(params, option, ids);
  });
  return params;
};

export const addRangeSearchParams = (
  searchParams: URLSearchParams,
  ranges: Filters['ranges'] = {}
): URLSearchParams => {
  const params = createSearchParams(searchParams);
  const paramsKeys = Array.from(params.keys());
  const rangeKeys = paramsKeys.filter((key) => /\.(to|from|excluded)$/.test(key));

  rangeKeys.forEach((key) => params.delete(key));

  Object.entries(ranges).forEach(([key, range]) => {
    if (range.from != null) {
      params.set(`${key}.from`, `${range.from}`);
    }
    if (range.to != null) {
      params.set(`${key}.to`, `${range.to}`);
    }
    if (range.excluded) {
      params.set(`${key}.excluded`, `${range.excluded}`);
    }
  });

  return params;
};

export const addIssuersSearchParams = (searchParams: URLSearchParams, filters: Filters): URLSearchParams => {
  const { tagTypes } = splitFiltersIntoTypeGroups(COMPANY_FILTER_GROUPS);

  let params = searchParams;

  params = addTagSearchParams(params, filters.tags, tagTypes);
  params = addRangeSearchParams(params, filters.ranges);
  params = addSearchParams(params, CSViewOption, filters.recWeightings || []);
  params = addSearchParams(params, CoverageOption, filters.coverage || []);

  if (filters.searchText) {
    params.set(SearchTextOption, filters.searchText.trim());
  } else {
    params.delete(SearchTextOption);
  }
  if (filters.watchlistId) {
    params.set(WatchListOption, filters.watchlistId);
  } else {
    params.delete(WatchListOption);
  }

  params.set(CoverageOperationOption, filters.coverageOperation || 'or');
  params.set('page', '1');

  return params;
};

export const parseFiltersFromSearchParams = (searchParams: URLSearchParams): Filters => {
  const { tagTypes, rangeTypes } = splitFiltersIntoTypeGroups(COMPANY_FILTER_GROUPS);
  const tagFilterTypes = tagTypes.filter(isOptionOfAnyFilterType);
  const filterTags = tagFilterTypes.reduce((acc, filterType) => {
    const tags = searchParams.getAll(filterType);

    if (tags.length) {
      acc[filterType] = tags;
    }

    return acc;
  }, {});

  const filterRanges: Filters['ranges'] = rangeTypes.reduce((acc, curr) => {
    const from = searchParams.get(`${curr}.from`);
    const to = searchParams.get(`${curr}.to`);
    const excluded = searchParams.get(`${curr}.excluded`);

    return {
      ...acc,
      [curr]: {
        from: from ? +from : undefined,
        to: to ? +to : undefined,
        excluded: excluded === 'true',
      },
    };
  }, {});

  return {
    tags: filterTags,
    recWeightings: searchParams.getAll(CSViewOption),
    ranges: filterRanges,
    coverage: searchParams.getAll(CoverageOption),
    searchText: searchParams.get(SearchTextOption) || '',
    watchlistId: searchParams.get(WatchListOption) ?? undefined,
    coverageOperation: (searchParams.get(CoverageOperationOption) as Filters['coverageOperation']) || 'or',
  };
};

const RANGE_MIN = 0;
const RANGE_MAX = 100;

export const validateFilters = (filters: Filters | null): boolean =>
  !!filters &&
  Object.values(filters.ranges || {}).every(({ from, to, isFractional }) =>
    validateRangeValues(from, to, RANGE_MIN, RANGE_MAX, isFractional)
  );

export const updateInvalidFilters = (filters: Filters): Filters => {
  const newRanges = Object.entries(filters.ranges || {}).reduce<Record<string, FilterRange>>((acc, [key, range]) => {
    const isValid = validateRangeValues(range.from, range.to, RANGE_MIN, RANGE_MAX);

    if (isValid) {
      return { ...acc, [key]: range };
    }

    return acc;
  }, {});

  return {
    ...filters,
    ranges: newRanges,
  };
};

export const mergeFiltersState = <T extends keyof Filters>(
  prevState: Filters,
  key: T,
  nextPartial: Filters[T]
): Filters => {
  if (isPlainObject(nextPartial)) {
    return {
      ...prevState,
      [key]: {
        ...((prevState[key] as object) || {}),
        ...(nextPartial as object),
      },
    };
  }

  return {
    ...prevState,
    [key]: nextPartial,
  };
};

export const isIssuersFilterApplied = (filters: Filters | null): boolean => {
  if (!filters) {
    return false;
  }

  return (
    !isEmpty(filters.recWeightings) ||
    !isEmpty(filters.coverage) ||
    Object.values(filters.tags || {}).some((t) => !isEmpty(t)) ||
    Object.values(filters.ranges || {}).some((r) => r.from != null || r.to != null || r.excluded)
  );
};
